import { useState } from 'react';
import { useComposerContext } from '../context/ComposerContext';
import { useMailRuntime } from '../context/MailRuntime';
import { composerRequest } from '../composerRequest';
import buildEmailHtml from '../email/buildEmailHtml';

export default function useDelivery({ capture, saveDraftLocal }) {
  const ctx = useComposerContext(), runtime = useMailRuntime();
  const key = `bc-delivery-${runtime.storageScope || 'author'}-${ctx.owner}`;
  const readPending = () => {
    const raw = localStorage.getItem(key); if (!raw) return null;
    const item = JSON.parse(raw);
    if (item.owner !== ctx.owner || !item.requestId || !item.newsletterId || !['test', 'campaign'].includes(item.mode)) throw Error('Delivery recovery is unreadable. Check Sent before starting another delivery.');
    return item;
  };
  const [pendingDelivery, setPendingDelivery] = useState(() => { try { return readPending(); } catch { return { corrupt: true }; } });
  const [deliveryResult, setDeliveryResult] = useState(null);
  const sendNewsletter = async ({ mode, action = 'start' } = {}) => {
    if (runtime.canSend === false) { ctx.setNotice('Newsletter delivery is not enabled in this workspace.'); return; }
    if (ctx.operationLock.current || !ctx.ready) return;
    ctx.operationLock.current = true; ctx.setSending(true); setDeliveryResult(null);
    try {
      let pending = readPending(), payload;
      if (action === 'start') {
        if (pending) throw Error('Check or continue the existing delivery before starting another one.');
        if (!['test', 'campaign'].includes(mode)) throw Error('Choose Test Email or Send to Subscribers.');
        if (!ctx.subject.trim()) throw Error('Add a subject before sending.');
        if (mode === 'test' && !/^[^\s<>@,;]+@[^\s<>@,;]+\.[^\s<>@,;]+$/.test(ctx.recipients.trim())) throw Error('Enter one valid test email address.');
        if (mode === 'campaign' && ctx.selectedList !== 'subscribers-list') throw Error('Choose All Subscribers before sending.');
        const snapshot = capture(), id = ctx.newsletterId || crypto.randomUUID();
        ctx.setNewsletterId(id);
        saveDraftLocal({ newsletterId: id });
        const saved = await composerRequest(runtime, ctx.owner, runtime.api.API_SAVE_NEWSLETTER, 'POST', { ...snapshot, newsletterId: id, version: ctx.version });
        if (saved.newsletterId !== id || !saved.version) throw Error('Draft save could not be confirmed. Reopen the saved copy before sending.');
        ctx.setVersion(saved.version || null);
        pending = { owner: ctx.owner, requestId: crypto.randomUUID(), newsletterId: id, mode, subject: snapshot.subject, testRecipient: mode === 'test' ? ctx.recipients.trim() : '' };
        payload = { ...pending, action: 'start', version: saved.version, selectedList: mode === 'campaign' ? ctx.selectedList : '', body: buildEmailHtml({ ...snapshot.design, bodyHtml: snapshot.body, subject: snapshot.subject, preheaderText: snapshot.preheaderText, footer: snapshot.footer, blockStyles: snapshot.blockStyles }) };
        // Keep the original request for safe recovery if the response is lost before a job exists.
        pending.payload = payload;
        localStorage.setItem(key, JSON.stringify(pending));
        setPendingDelivery(pending);
      } else {
        if (!pending || pending.corrupt) throw Error('No readable delivery is available to continue.');
        payload = { newsletterId: pending.newsletterId, requestId: pending.requestId, mode: pending.mode, action };
        if (action === 'continue' && pending.payload) payload = pending.payload;
      }
      const result = await composerRequest(runtime, ctx.owner, runtime.api.API_SEND_NEWSLETTER, 'POST', payload);
      if (result.mode !== pending.mode || typeof result.complete !== 'boolean') throw Error('Delivery result is uncertain. Check this delivery before sending again.');
      setDeliveryResult(result);
      if (result.complete) { localStorage.removeItem(key); setPendingDelivery(null); }
      else { delete pending.payload; localStorage.setItem(key, JSON.stringify(pending)); setPendingDelivery({ ...pending }); }
      ctx.setNotice(result.mode === 'test' && result.complete && result.status === 'sent' ? 'Test email accepted for delivery. Your draft is still open.' : result.message || (result.complete ? 'Delivery attempt finished. Review the results below. Your draft is preserved.' : 'Delivery is unfinished. Check progress or continue the saved delivery below.'));
    } catch (error) {
      if (action === 'start' && [400, 401, 403, 404].includes(error.status)) {
        localStorage.removeItem(key); setPendingDelivery(null);
      }
      ctx.setNotice(error.message || 'Delivery could not be confirmed. Your draft is preserved.');
    }
    finally { ctx.operationLock.current = false; ctx.setSending(false); }
  };
  return { sendNewsletter, pendingDelivery, deliveryResult };
}
